import { useState, useEffect } from 'react';
import Papa from 'papaparse';

import marketCsvUrl from '../assets/set_mc.csv?url';
import logoCsvUrl from '../assets/ThaiCompanyData.csv?url';

export const useStockData = () => {
  const [stockDataMap, setStockDataMap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadStockData = async () => {
      try {
        console.log(' Loading SET stock data...');
        
        const [marketResponse, logoResponse] = await Promise.all([
          fetch(marketCsvUrl), 
          fetch(logoCsvUrl)  
        ]);
        
        if (!marketResponse.ok) {
          throw new Error('Failed to fetch set_mc.csv');
        }
        
        const marketText = await marketResponse.text();
        const logoText = logoResponse.ok ? await logoResponse.text() : '';

        let logoMap = new Map();
        
        if (logoText) {
          Papa.parse(logoText, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim(),
            complete: (logoResults) => {
              console.log('   Logo CSV parsed:', logoResults.data.length, 'rows');
              
              logoResults.data.forEach((row) => {
                if (row.symbol && row.logo) {
                  const symbol = row.symbol.trim().toUpperCase();
                  const logoUrl = row.logo.trim();
                  
                  if (logoUrl && logoUrl.startsWith('http')) {
                    logoMap.set(symbol, logoUrl);
                  }
                }
              });
              
              console.log(' Valid logos found:', logoMap.size);
              
              if (logoMap.size > 0) {
                const samples = Array.from(logoMap.entries()).slice(0, 3);
                console.log(' Sample logos:', samples.map(([sym, url]) => `${sym}: ${url.substring(0, 60)}...`));
              }
            }
          });
        }

        Papa.parse(marketText, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim(),
          complete: (results) => {
            const stockMap = new Map();
            
            results.data.forEach((row) => {
              if (row.SymbolName && typeof row.SymbolName === 'string') {
                const symbol = row.SymbolName.trim().toUpperCase();
                const logoUrl = logoMap.get(symbol);
                
                stockMap.set(symbol, {
                  name: row.SymbolName,
                  description: row.CompanyNameEN || row.CompanyNameTH || symbol,
                  exchange: row.MarketName || row.MarketID || 'SET',
                  sector: row.SectorName || row.IndustryName || 'N/A',
                  market_cap: row.MarketCap,
                  logo_url: logoUrl || null,
                  companyNameTH: row.CompanyNameTH,
                  companyNameEN: row.CompanyNameEN,
                  listedShares: row.ListedShares,
                  industryName: row.IndustryName,
                  sectorName: row.SectorName
                });
              }
            });
            
            const withLogos = Array.from(stockMap.values()).filter(s => s.logo_url).length;
            
            setStockDataMap(stockMap);
            setIsLoading(false);
            
            console.log(' SET stock data loaded:', stockMap.size, 'symbols');
            console.log('    Symbols with logos:', withLogos);
            
            if (withLogos > 0) {
              const samples = Array.from(stockMap.entries())
                .filter(([_, data]) => data.logo_url)
                .slice(0, 3);
              console.log(' Sample stocks with logos:');
              samples.forEach(([symbol, data]) => {
                console.log(`  ${symbol}: ${data.logo_url?.substring(0, 60)}...`);
              });
            }
          },
          error: (err) => {
            console.error(' Error parsing market CSV:', err);
            setError(err.message);
            setIsLoading(false);
          }
        });
      } catch (err) {
        console.error(' Error loading data:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    loadStockData();
  }, []);

  return { stockDataMap, isLoading, error };
};